import { useState } from 'react';
import clsx from 'clsx';
import { useLocation, useNavigate, useOutletContext } from 'react-router';

import useSignupQuizQuery from 'api/queries/useSignupQuizQuery';
import { SignupQuizSteps } from 'constants/signup';
import { IMPERFECT_THEME_DOMAIN, MISFITS_THEME_DOMAIN } from 'constants/theme';
import { useAmplitudeExperiment } from 'contexts/AmplitudeExperimentContext';
import { CardInput } from 'honeydew/CardInput';
import Checkbox from 'honeydew/Checkbox';
import { AisleIcon } from 'honeydew/icons/Aisles';
import Loader from 'honeydew/Loader';
import { FEATURE_FLAG_NAMES } from 'hooks/useFeatureFlag';
import useGlobalStore from 'hooks/useGlobalStore';
import { useIsSyntheticsUser } from 'hooks/useIsSyntheticsUser';
import useSignupRedirect from 'hooks/useSignupRedirect';
import useTrackPageView from 'hooks/useTrackPageView';
import { FEATURE_FLAG_VARIANTS } from 'pages/signup/registration/constants';
import SignupFooter from 'pages/signup/shared/SignupFooter';
import { trackEvent } from 'utils/analyticsUtils';
import { getPackIconName, getQuizAllowedPaths, getSignupLocalStorage, setSignupLocalStorage } from 'utils/signupUtils';

function WhatDoYouBuy() {
    const { handleContinueClick } = useOutletContext();
    const navigate = useNavigate();
    const { experimentClientIsReady, getFeatureFlagVariant } = useAmplitudeExperiment();
    const isSyntheticsUser = useIsSyntheticsUser();

    const allowedPaths = getQuizAllowedPaths(SignupQuizSteps.WHAT_DO_YOU_BUY);
    const signupLocalStorage = getSignupLocalStorage();
    const whatDoYouBuyStorage = signupLocalStorage?.whatDoYouBuy;
    const { isMisfitsTheme } = useGlobalStore();
    const { pathname } = useLocation();

    useSignupRedirect({ allowedPaths });
    useTrackPageView('signup quiz page view');

    const [planSelections, setPlansSelections] = useState(whatDoYouBuyStorage?.planIDs ?? []);
    const [allCheckbox, setAllCheckbox] = useState(false);

    const { data: quizData = {} } = useSignupQuizQuery({
        funnelType: isMisfitsTheme ? 'misfits' : 'imperfect',
        quizResults: {
            dietaryPreference: signupLocalStorage.dietaryPreference,
            household: signupLocalStorage.whoAreYouShoppingFor,
            percentOrganic: signupLocalStorage.percentOrganic,
        },
    });

    const plans = quizData?.plans;
    const centerLastCardInput = plans?.length > 0 ? plans.length % 3 === 1 : false;

    const persistWhatDoYouBuy = (planIDs) => {
        setSignupLocalStorage({
            whatDoYouBuy: {
                planIDs,
                planNames: plans
                    ? plans.filter((plan) => planIDs.includes(plan.id)).map((plan) => plan.name)
                    : getSignupLocalStorage()?.whatDoYouBuy?.planNames,
            },
        });
    };

    const handlePlanChange = (value) => {
        let newPlanSelections = [];

        if (planSelections.includes(value)) {
            newPlanSelections = planSelections.filter((planSelection) => planSelection !== value);
        } else {
            newPlanSelections = [...planSelections, value];
        }

        setPlansSelections(newPlanSelections);
        persistWhatDoYouBuy(newPlanSelections);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        trackEvent('quiz step complete', {
            pathname,
            question: SignupQuizSteps.WHAT_DO_YOU_BUY,
            response: planSelections,
        });

        persistWhatDoYouBuy(planSelections);

        const registrationVariant =
            experimentClientIsReady && !isSyntheticsUser
                ? getFeatureFlagVariant(FEATURE_FLAG_NAMES.FUNNEL_REGISTRATION_TEST_V2, 'control')
                : undefined;

        if (
            registrationVariant === FEATURE_FLAG_VARIANTS.OLD_SUMMARY_NEW_FORMS ||
            registrationVariant === FEATURE_FLAG_VARIANTS.NEW_SUMMARY_NEW_FORMS
        ) {
            setSignupLocalStorage({ path: '/join/registration' });
            navigate('/join/registration');
            return;
        }

        handleContinueClick();
    };

    const handleAllCheckboxChange = (checked) => {
        const newPlanSelections = checked ? plans.filter((plan) => plan.active).map((plan) => plan.id) : [];
        setPlansSelections(newPlanSelections);
        setAllCheckbox(checked);
        persistWhatDoYouBuy(newPlanSelections);
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="mx-auto max-w-[600px] px-25 pt-25 pb-[273px] sm:pb-[249px]">
                <h1 className="text-heading-sm md:text-center" data-optimizely-id="what-do-you-buy-header">
                    What do you buy each week?
                </h1>
                {plans?.length > 0 ? (
                    <>
                        <p
                            className="text-body-lg my-20 md:text-center"
                            data-optimizely-id="what-do-you-buy-sub-header"
                        >
                            Select all that apply
                        </p>
                        <div className="mb-15 rounded-md border border-grey-light bg-white p-5 md:flex md:justify-center">
                            <Checkbox
                                checked={allCheckbox}
                                id="all-plans"
                                label="A bit of everything"
                                name="all"
                                labelStyle="bold"
                                onCheckedChange={handleAllCheckboxChange}
                            />
                        </div>
                        <div className="grid grid-cols-3 gap-10">
                            {plans.map((plan, index) => (
                                <CardInput
                                    as="checkbox"
                                    inputAttributes={{
                                        name: `plan-${plan.id}`,
                                        value: plan.id || '0',
                                        handleChange: handlePlanChange,
                                    }}
                                    disabled={!plan.active}
                                    checked={planSelections.includes(plan.id)}
                                    extendedStyles={{
                                        wrapper: clsx({
                                            'col-start-2': centerLastCardInput && index === plans.length - 1,
                                        }),
                                    }}
                                    key={plan.id}
                                >
                                    <div className="flex flex-col items-center gap-5 text-center">
                                        <AisleIcon
                                            aisleIconName={getPackIconName(plan.name)}
                                            isActive={plan.active}
                                            width={50}
                                            brand={isMisfitsTheme ? MISFITS_THEME_DOMAIN : IMPERFECT_THEME_DOMAIN}
                                        />
                                        <p className="font-grotesk-bold text-body-mobile-md">{plan.name}</p>
                                    </div>
                                </CardInput>
                            ))}
                        </div>
                    </>
                ) : (
                    <div className="mt-40 flex justify-center">
                        <Loader />
                    </div>
                )}
            </div>
            <SignupFooter
                isSubmitButtonDisabled={!plans?.length || planSelections.length === 0}
                infoBubbleTitle={<span data-optimizely-id="what-do-you-buy-title">SERIOUSLY FLEXIBLE</span>}
                infoBubbleDescription={
                    <span data-optimizely-id="what-do-you-buy-description">
                        <span className="font-grotesk-bold">
                            You&apos;ll always have access to our entire assortment.{' '}
                        </span>
                        Your selections here just help us recommend personalized items for your cart!
                    </span>
                }
                hideInfoBubble={false}
                showScrollDownButton={false}
            />
        </form>
    );
}

export { WhatDoYouBuy as Component };
